<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  block: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  dataSources: { type: Array, default: () => [] },
});

defineEmits(['select', 'delete']);

const { t } = useI18n();

// Keys are the real PortalBlock.block_type enum values. Dynamic blocks get the teal
// accent, static blocks the violet accent.
const BLOCK_TYPE_STYLES = {
  list: { class: 'text-teal-400 bg-teal-400/10' },
  table: { class: 'text-teal-400 bg-teal-400/10' },
  item_details: { class: 'text-teal-400 bg-teal-400/10' },
  summary_card: { class: 'text-teal-400 bg-teal-400/10' },
  ticket_list: { class: 'text-teal-400 bg-teal-400/10' },
  ticket_detail: { class: 'text-teal-400 bg-teal-400/10' },
  header: { class: 'text-violet-400 bg-violet-400/10' },
  hero: { class: 'text-violet-400 bg-violet-400/10' },
  cta: { class: 'text-violet-400 bg-violet-400/10' },
  quick_links: { class: 'text-violet-400 bg-violet-400/10' },
};

// Maps each enum block_type to its palette i18n label key.
const BLOCK_TYPE_LABEL_KEYS = {
  list: 'APP_BUILDER.PALETTE.LIST',
  table: 'APP_BUILDER.PALETTE.TABLE',
  item_details: 'APP_BUILDER.PALETTE.DETAIL',
  summary_card: 'APP_BUILDER.PALETTE.STAT',
  ticket_list: 'APP_BUILDER.PALETTE.SECTION_TICKETS',
  ticket_detail: 'APP_BUILDER.PALETTE.SECTION_TICKETS',
  header: 'APP_BUILDER.PALETTE.HEADING',
  hero: 'APP_BUILDER.PALETTE.HERO',
  cta: 'APP_BUILDER.PALETTE.CTA',
  quick_links: 'APP_BUILDER.PALETTE.QUICK_LINKS',
};

const typeStyle = computed(
  () => BLOCK_TYPE_STYLES[props.block.block_type] || BLOCK_TYPE_STYLES.header
);

const typeLabel = computed(() => {
  const key =
    BLOCK_TYPE_LABEL_KEYS[props.block.block_type] || 'APP_BUILDER.PALETTE.LIST';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(key);
});

const isDynamic = computed(() => !!props.block.dynamic);

// Friendly entity name per source_model, mirroring the inspector source picker.
const SOURCE_MODEL_LABEL_KEYS = {
  Ticket: 'APP_BUILDER.INSPECTOR.SOURCES.TICKET',
  Contract: 'APP_BUILDER.INSPECTOR.SOURCES.CONTRACT',
  ServiceOrder: 'APP_BUILDER.INSPECTOR.SOURCES.SERVICE_ORDER',
};

// Resolves the bound data source to its human label + entity ("My Tickets · Tickets")
// rather than leaking the internal key ("tickets-2") into the canvas.
const boundSource = computed(() =>
  props.dataSources.find(ds => ds.key === props.block.data_source_key)
);

const boundSourceLabel = computed(() => {
  const ds = boundSource.value;
  if (!ds) return props.block.data_source_key;
  const modelKey = SOURCE_MODEL_LABEL_KEYS[ds.source_model];
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const entity = modelKey ? t(modelKey) : ds.source_model;
  const label = ds.label || ds.key;
  return entity && entity !== label ? `${label} · ${entity}` : label;
});

// Bare enum values ('public'/'logged_in'/'group') — what the backend serializes.
const visibilityLabel = computed(() => {
  switch (props.block.visibility) {
    case 'logged_in':
      return t('APP_BUILDER.INSPECTOR.VISIBILITY.LOGGED_IN');
    case 'group':
      return t('APP_BUILDER.INSPECTOR.VISIBILITY.GROUP');
    default:
      return t('APP_BUILDER.INSPECTOR.VISIBILITY.PUBLIC');
  }
});
</script>

<template>
  <div
    class="rounded-lg border bg-n-background p-3 flex flex-col gap-0 group transition-colors cursor-pointer"
    :class="[
      selected
        ? 'border-woot-500/60 ring-1 ring-woot-500/30'
        : isDynamic
          ? 'border-teal-500/30 hover:border-teal-500/50'
          : 'border-n-weak hover:border-n-strong',
    ]"
    @click="$emit('select')"
  >
    <div class="flex items-start gap-3">
      <!-- Drag handle -->
      <div
        class="app-block-drag-handle text-n-slate-8 hover:text-n-slate-10 mt-0.5 opacity-30 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </div>

      <!-- Block content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <span
            class="text-xs font-medium px-1.5 py-0.5 rounded shrink-0"
            :class="typeStyle.class"
          >
            {{ typeLabel }}
          </span>
          <span
            v-if="isDynamic"
            class="text-[10px] font-medium text-teal-400 shrink-0"
          >
            {{ t('APP_BUILDER.CANVAS.DYNAMIC_BADGE') }}
          </span>
        </div>

        <!-- Data source binding -->
        <div v-if="isDynamic" class="text-[11px] text-n-slate-10 truncate">
          <template v-if="block.data_source_key">
            {{ t('APP_BUILDER.CANVAS.BOUND_TO', { source: boundSourceLabel }) }}
          </template>
          <span v-else class="text-amber-400">
            {{ t('APP_BUILDER.CANVAS.NO_SOURCE') }}
          </span>
        </div>
        <div v-else class="text-[11px] text-n-slate-10 truncate">
          {{
            block.config && block.config.text
              ? block.config.text
              : t('APP_BUILDER.CANVAS.STATIC_HINT')
          }}
        </div>

        <!-- Visibility badge -->
        <div class="flex items-center gap-1.5 mt-1.5">
          <span
            class="inline-flex items-center gap-1 text-[10px] font-medium text-n-slate-11 bg-n-alpha-2 px-1.5 py-0.5 rounded"
          >
            {{ visibilityLabel }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div
        class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
      >
        <button
          class="w-6 h-6 rounded flex items-center justify-center text-n-slate-9 hover:text-red-400 hover:bg-n-alpha-2"
          @click.stop="$emit('delete')"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
